/**
 * Read-only summary of a config.json (and, for step functions, its definition.json),
 * used by the catalog entity "Configuration" card to preview an item's shape without
 * opening the full Config Editor. Isomorphic + pure so it can be unit-tested and reused.
 *
 * Deliberately tolerant of every template: it walks the known lambda-group keys and also
 * accepts a flat `Record<name, lambda>` group (valkyrie `queue_consumer_lambdas`).
 *
 * For each field it resolves the *effective* value the way Terraform would — explicit value
 * on the lambda -> group `defaults` block -> module default — and records whether the shown
 * value was inherited (came from defaults / module) so the card can badge it. The phx-api
 * resolution mirrors `normalizeApiConfig` (authorizer snapstart/snap_alias default true;
 * snap_alias forced true whenever snapstart is on). For step-function `managed_lambdas` there
 * is no group `defaults` block; only the snapstart/snap_alias module defaults are known here
 * (matching terraform-phxp-step-function's false/false), so memory/timeout/runtime are left
 * blank rather than guessed.
 */

import type { ConfigTargetType } from './projects';
import {
  DEFAULT_RUNTIME,
  DEFAULT_MEMORY,
  DEFAULT_TIMEOUT,
  DEFAULT_SNAPSTART_ENABLED,
  DEFAULT_SNAP_ALIAS_ENABLED,
} from './normalizeApiConfig';

/** Fields whose displayed value can be inherited from defaults / module. */
export type InheritableField = 'runtime' | 'memorySize' | 'timeout' | 'snapstart' | 'snapAlias';

export interface LambdaSummary {
  name: string;
  /** endpoint | authorizer | queue-consumer | managed */
  group: string;
  runtime?: string;
  memorySize?: number;
  timeout?: number;
  snapstart?: boolean;
  snapAlias?: boolean;
  handler?: string;
  capacityProvider?: string;
  /** Fields whose shown value came from group defaults / module default, not the lambda itself. */
  inherited: Partial<Record<InheritableField, boolean>>;
}

export interface ConfigSummary {
  type: ConfigTargetType;
  templateName?: string;
  lambdas: LambdaSummary[];
  /** step-function only: number of states in definition.json */
  stateCount?: number;
  /** step-function only: state names (top-level) */
  stateNames?: string[];
  /** step-function only: IAM policy names attached to the state machine role */
  iamPolicyNames?: string[];
}

/** Lambda-group container keys and the label shown for lambdas found under them. */
const LAMBDA_GROUPS: { key: string; label: string }[] = [
  { key: 'endpoint_lambdas', label: 'endpoint' },
  { key: 'authorizer_lambdas', label: 'authorizer' },
  { key: 'queue_consumer_lambdas', label: 'queue-consumer' },
];

export const isRecord = (v: unknown): v is Record<string, any> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

/** A lambda entry is any object that looks like a function definition. */
const looksLikeLambda = (v: unknown): boolean =>
  isRecord(v) &&
  ('lambda_handler' in v ||
    'runtime' in v ||
    'memory_size' in v ||
    'lambda_source' in v ||
    'timeout' in v);

const isPhxApiTemplate = (templateName?: string) => !templateName || templateName === 'phx-api-v2';

/** Module-level fallbacks Terraform applies when neither the lambda nor group defaults set a value. */
interface ModuleDefaults {
  runtime?: string;
  memorySize?: number;
  timeout?: number;
  snapstart?: boolean;
  snapAlias?: boolean;
}

const moduleDefaultsFor = (
  type: ConfigTargetType,
  templateName: string | undefined,
  group: string,
): ModuleDefaults => {
  // terraform-phxp-step-function managed lambdas: no group defaults; only snapstart/snap_alias
  // module defaults are known in this repo (matching the editor's DEFAULT_* constants).
  if (type === 'step-function' && group === 'managed') {
    return {
      snapstart: DEFAULT_SNAPSTART_ENABLED,
      snapAlias: DEFAULT_SNAP_ALIAS_ENABLED,
    };
  }

  // terraform-phx-api: authorizers default snapstart/snap_alias to true; endpoints to the constants.
  if (type === 'api' && isPhxApiTemplate(templateName)) {
    const isAuthorizer = group === 'authorizer';
    return {
      runtime: DEFAULT_RUNTIME,
      memorySize: DEFAULT_MEMORY,
      timeout: DEFAULT_TIMEOUT,
      snapstart: isAuthorizer ? true : DEFAULT_SNAPSTART_ENABLED,
      snapAlias: isAuthorizer ? true : DEFAULT_SNAP_ALIAS_ENABLED,
    };
  }

  // Other templates (valkyrie / condor): group-defaults inheritance still applies, but we don't
  // fabricate module fallbacks we can't verify from this repo.
  return {};
};

interface Resolved<T> {
  value?: T;
  inherited: boolean;
}

const nullish = (v: unknown): boolean => v === undefined || v === null;

/** own value -> group default -> module default, tracking whether the winner was inherited. */
const resolveField = <T>(
  own: T | undefined,
  groupDefault: T | undefined,
  moduleDefault: T | undefined,
): Resolved<T> => {
  if (!nullish(own)) return { value: own, inherited: false };
  if (!nullish(groupDefault)) return { value: groupDefault, inherited: true };
  if (!nullish(moduleDefault)) return { value: moduleDefault, inherited: true };
  return { value: undefined, inherited: false };
};

const summarizeLambda = (
  name: string,
  group: string,
  lambda: Record<string, any>,
  defaults: Record<string, any> | undefined,
  mod: ModuleDefaults,
): LambdaSummary => {
  const runtime = resolveField<string>(lambda.runtime, defaults?.runtime, mod.runtime);
  const memorySize = resolveField<number>(
    lambda.memory_size,
    defaults?.memory_size,
    mod.memorySize,
  );
  const timeout = resolveField<number>(lambda.timeout, defaults?.timeout, mod.timeout);
  const snapstart = resolveField<boolean>(lambda.snapstart, defaults?.snapstart, mod.snapstart);

  // SnapStart requires the alias, so Terraform forces snap_alias on whenever snapstart resolves
  // true — unless the lambda set snap_alias explicitly. Otherwise fall through the normal chain.
  let snapAlias: Resolved<boolean>;
  if (!nullish(lambda.snap_alias)) {
    snapAlias = { value: lambda.snap_alias, inherited: false };
  } else if (snapstart.value === true) {
    snapAlias = { value: true, inherited: true };
  } else {
    snapAlias = resolveField<boolean>(undefined, defaults?.snap_alias, mod.snapAlias);
  }

  const inherited: Partial<Record<InheritableField, boolean>> = {};
  if (runtime.inherited) inherited.runtime = true;
  if (memorySize.inherited) inherited.memorySize = true;
  if (timeout.inherited) inherited.timeout = true;
  if (snapstart.inherited) inherited.snapstart = true;
  if (snapAlias.inherited) inherited.snapAlias = true;

  return {
    name,
    group,
    runtime: runtime.value,
    memorySize: memorySize.value,
    timeout: timeout.value,
    snapstart: snapstart.value,
    snapAlias: snapAlias.value,
    handler: lambda.lambda_handler ?? defaults?.lambda_handler,
    capacityProvider: lambda.capacity_provider ?? defaults?.capacity_provider,
    inherited,
  };
};

/** Extract lambdas from one group value, which may be `{lambdas, defaults}` or a flat record. */
const lambdasFromGroup = (
  groupValue: unknown,
  label: string,
  type: ConfigTargetType,
  templateName: string | undefined,
): LambdaSummary[] => {
  if (!isRecord(groupValue)) return [];
  const mod = moduleDefaultsFor(type, templateName, label);

  // Wrapped shape: { lambdas: {...}, defaults: {...} }
  if (isRecord(groupValue.lambdas)) {
    const defaults = isRecord(groupValue.defaults) ? groupValue.defaults : undefined;
    return Object.entries(groupValue.lambdas).map(([name, l]) =>
      summarizeLambda(name, label, isRecord(l) ? l : {}, defaults, mod),
    );
  }

  // Flat shape: { <name>: <lambda>, ... } (e.g. valkyrie queue_consumer_lambdas)
  return Object.entries(groupValue)
    .filter(([, l]) => looksLikeLambda(l))
    .map(([name, l]) => summarizeLambda(name, label, l as Record<string, any>, undefined, mod));
};

/**
 * State names from a step-function definition.json. Returns `undefined` when the definition is absent
 * or unparseable (state count UNKNOWN) so the caller can tell that apart from a genuinely empty States
 * map (0 states) — otherwise a fetch failure would be misreported as "0 states".
 */
const parseDefinition = (definition?: string): string[] | undefined => {
  if (!definition) return undefined;
  try {
    const parsed = JSON.parse(definition);
    if (isRecord(parsed) && isRecord(parsed.States)) {
      return Object.keys(parsed.States);
    }
  } catch {
    // Definition may contain unresolved CloudFormation placeholders that break JSON.parse.
    // Return undefined (unknown) rather than 0 so the card doesn't misreport the size.
    return undefined;
  }
  return undefined;
};

const normalizeStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.map(String) : [];

/**
 * Build a display summary from a loaded config. Never throws: a malformed config just
 * yields an empty lambda list so the card can still render the "Open in Config Editor" link.
 */
export function summarizeConfig(input: {
  type: ConfigTargetType;
  config: unknown;
  templateName?: string;
  /** raw definition.json content (step functions only) */
  definition?: string;
}): ConfigSummary {
  const { type, config, templateName, definition } = input;
  const cfg = isRecord(config) ? config : {};

  // A standalone lambda (phx-lambda-v1) config.json IS a single Lambda object, not a name->map.
  // Summarize it as one lambda. No group `defaults` and no fabricated module fallbacks here — the
  // config stores its fields explicitly (it's near-passthrough to terraform-aws-lambda v5), and
  // snap_alias doesn't exist for standalone lambdas (the module auto-aliases on snapstart).
  if (type === 'lambda') {
    const name = typeof cfg.function_name === 'string' ? cfg.function_name : 'lambda';
    return {
      type,
      templateName,
      lambdas: [summarizeLambda(name, 'standalone', cfg, undefined, {})],
    };
  }

  const lambdas: LambdaSummary[] = [];
  for (const { key, label } of LAMBDA_GROUPS) {
    lambdas.push(...lambdasFromGroup(cfg[key], label, type, templateName));
  }

  if (type === 'step-function') {
    // managed_lambdas is `{ iam_policy_names, lambdas }` (canonical) — reuse the group extractor.
    lambdas.push(...lambdasFromGroup(cfg.managed_lambdas, 'managed', type, templateName));
    const stateNames = parseDefinition(definition);
    return {
      type,
      templateName,
      lambdas,
      // undefined => unknown (definition missing/unparseable), distinct from 0 states.
      stateCount: stateNames?.length,
      stateNames: stateNames ?? [],
      iamPolicyNames: normalizeStringArray(cfg.step_function_iam_policy_names),
    };
  }

  return { type, templateName, lambdas };
}
